/**
 * CoinW timeframe configuration and interval mapping.
 *
 * CoinW historical candles come from `command=returnChartData&period=<seconds>`.
 * Supported periods (seconds), per connman `market/rest/connector.go intervalMap`:
 *   60 (1m), 300 (5m), 900 (15m), 1800 (30m), 3600 (1h), 14400 (4h),
 *   86400 (1d), 604800 (1w).
 * CoinW has NO kline WebSocket channel — live bars are aggregated from the
 * public trades stream.
 */

import {
  registerExchangeTimeframes,
  resolutionToSeconds,
  type TimeframeConfig,
} from '../../lib/timeframes';

/** Native CoinW kline periods, expressed in `<n><unit>` form for the registry. */
export const COINW_TIMEFRAMES: TimeframeConfig = {
  native: ['1min', '5min', '15min', '30min', '1hour', '4hour', '1day', '1week'],
  synthetic: ['1s'], // Sub-minute updates come from trades/ticker.
  minIntervalSeconds: 60,
};

// Register with the central timeframe registry.
registerExchangeTimeframes('coinw', COINW_TIMEFRAMES);

/** CoinW `returnChartData` supported periods in seconds. */
const COINW_PERIOD_SECONDS = [60, 300, 900, 1800, 3600, 14400, 86400, 604800] as const;

/**
 * Maps a TradingView resolution to the nearest supported CoinW `period`
 * (in seconds, returned as a string for the query argument).
 */
export const mapToCoinwInterval = (resolution: string): string => {
  const target = Math.max(resolutionToSeconds(resolution), 60);

  let best: number = COINW_PERIOD_SECONDS[0];
  for (const period of COINW_PERIOD_SECONDS) {
    if (Math.abs(period - target) < Math.abs(best - target)) {
      best = period;
    }
  }

  return String(best);
};
